



import { APP_BASE_HREF } from '@angular/common';
import { Component, Input, forwardRef, EventEmitter, Output, Inject, SimpleChanges, OnChanges, OnInit, OnDestroy } from '@angular/core';

import { of, Subscription, catchError, filter, map } from 'rxjs';
import { difference } from 'lodash';
import { DndDropEvent } from 'ngx-drag-drop';

import { AbstractFormFieldComponent } from '../../../PageBuilder/components/FormField/abstract-form-field.component';
import { SeriesPickerFieldModel } from '../../models/series-picker-field.model';
import { mentionSeriesTypes, SeriesTypeEnum, socialSeriesTypes } from '../../enums/series-type.enum';
import { ReportSeriesModel } from '../../models/report-series.model';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
import { ReportDateRangeModel } from '../../models/report-date-range.model';
import { monitoringDateFilter24HourOption } from '../../../../Monitor/Shared/models/monitoring-date-filter-options';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ReportsSeriesFilterService } from '../../services/reports-series-filters.service';
import { FilterService } from '../../../Filter/services/abstract-filter.service';
import { DateQueryDateTypeEnum } from '../../../../api/mentions/enum/date-query-date-type.enum';


export const reportSavedSourceTypes: SeriesTypeEnum[] = [
    SeriesTypeEnum.folder,
    SeriesTypeEnum.tag,
    SeriesTypeEnum.tagGroup,
    SeriesTypeEnum.aiAgent
];

@Component({
    selector: 'agpr-series-picker-field',
    templateUrl: './series-picker-field.component.html',
    styleUrls: ['./series-picker-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => SeriesPickerFieldComponent)
        },
        ReportsSeriesFilterService,
        {
            provide: FilterService,
            useExisting: ReportsSeriesFilterService
        }
    ],
    standalone: false
})
export class SeriesPickerFieldComponent extends AbstractFormFieldComponent implements OnChanges, OnInit, OnDestroy {
    readonly messageSeverityEnum = messageSeverityEnum;
    defaultColors: string[] = [];
    isBarChart: boolean = false;

    seriesList: ReportSeriesModel[];
    invalidSeriesIndexes: number[] = [];
    selectedCharts: string[];
    draggingSeries: ReportSeriesModel;

    hasActiveFilters: boolean = false;
    wereAllSavedSources: boolean = false;
    showPicker: boolean = true;
    isInputFocused: boolean = false;

    @Input() declare field: SeriesPickerFieldModel;
    @Input() declare value: ReportSeriesModel[];
    @Input() showLabel: boolean = true;
    @Input() showContent: boolean = true;
    @Input() isNewReport: boolean = false;
    @Input() showColorSwatch: boolean = false;
    @Input() ariaDescribedBy: string;
    @Input() showHiddenOption: boolean = true;
    @Output() changes: EventEmitter<ReportSeriesModel[]> = new EventEmitter<ReportSeriesModel[]>();

    readonly buttonTypes = ButtonTypesEnum;

    private allowDateRangeAll: boolean;

    private subscriptions: Subscription[] = [];

    _value: ReportSeriesModel[];

    constructor(
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private userPreferencesService: UserPreferencesService,
        private currentUserStateService: CurrentUserStateService,
        private coreDialogService: CoreDialogService,
        private filterService: ReportsSeriesFilterService
    ) {
        super();
    }

    ngOnInit() {
        this.selectedCharts = this.field.tooltipMessage;

        this.isBarChart = !!this.field?.isBarChart;
        const colorType = this.isBarChart ? 'barColors' : 'generalColors';

        this.subscriptions.push(
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors[colorType]),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors[colorType]))
            ).subscribe(generalColors => {
                this.defaultColors.length = 0;
                this.defaultColors.push(...Object.values(generalColors));
                const accentColor = this.block?.displayElement?.styles?.colorMap?.['accentColor'];
                if (accentColor) {
                    this.defaultColors[0] = accentColor;
                }
            }),
            this.currentUserStateService.getPermissions().pipe(
                filter(x => !!x)
            ).subscribe((permissions) => {
                // hide monitoring option types if you don't have access
                if (!permissions.includes(userPermissionsEnum.advancedMonitoring)) {
                    this.field.typeOptions = difference(this.field?.typeOptions, mentionSeriesTypes);
                }
                // hide social option types if you don't have access
                if (!permissions.includes(userPermissionsEnum.socialIntelligence)) {
                    this.field.typeOptions = difference(this.field?.typeOptions, socialSeriesTypes);
                }
            }),
            this.filterService.getFilters().subscribe(filters => {
                this.hasActiveFilters = !!filters.find(filterChip => filterChip.chips && filterChip.chips.length > 0);
            })
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('value')) {
            this.invalidSeriesIndexes = [];
            const displayElement = this.block?.displayElement as ChartDisplayElementModel;
            if (this.value && this.value.length > 0) {
                // removes the data series that are not included in the charts series types
                this.seriesList = this.value.filter(series => {
                    return !!this.field?.typeOptions?.find(type => type === series.type);
                });

                this.seriesList.forEach((series, i) => {
                    if (series.color && displayElement && (this.showColorSwatch || this.field?.showColorSwatch)) {
                        const key = `${ReportSeriesModel.getSeriesColorKeyPrefix() + i}`;
                        displayElement.styles.colorMap[key] = series.color;
                        if (this.isBarChart) {
                            displayElement.styles.colorMap[key] = series.id ? series.color : this.defaultColors[0];
                        }
                    }

                    if (!series.isValid()) {
                        this.invalidSeriesIndexes.push(i);
                    }
                });

            }
            // set series to default on empty
            if (this.seriesList?.length < 1 || this.value?.length < 1){
                this.seriesList = this.getDefaultSeriesList(this.field?.typeOptions[0]);
            }

            // if switching types from multiseries to single series, then remove the excess
            if (this.seriesList && !this.field?.multiSeries && this.seriesList.length > 1) {
                this.seriesList = this.seriesList.slice(0, 1);
            }

            this.checkDateRange();

            if (this.field.appearCondition) {
                this.showPicker = this.field.appearCondition(this.block.displayElement);
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }

    isDateRange24Hour(dateQuery: DateQueryModel): boolean {
        if (
            dateQuery.type === DateQueryTypeEnum.RELATIVE &&
            dateQuery.relativeStartOffsetH === -24 &&
            dateQuery.relativeEndOffsetH === 0
        ) {
            return true;
        }
        return false;
    }

    isDateRangeAll(dateQuery: DateQueryModel): boolean {
        return dateQuery.type === DateQueryTypeEnum.ALL;
    }

    /**
     * When swapping series types, you can be in a state where you have an invalid date range applied.
     * This checks and retroactively fixes these bad states, if applicable
     */
    checkDateRange(): void {
        this.allowDateRangeAll = !this.seriesList.find(
            series => !series.canUseAllDateRange()
        );

        if (!this.allowDateRangeAll && this.isDateRangeAll(this.seriesList[0].dateRange)) {
            const newDateQuery = new ReportDateRangeModel().setFromDateQuery(monitoringDateFilter24HourOption.value);
            this.seriesList.forEach(seriesItem => seriesItem.dateRange = newDateQuery);
        }
    }

    checkDateType(): void {
        // set date type options based on selected series types
        const areAllSavedSources = this.seriesList.every(series => reportSavedSourceTypes.includes(series.type));
        if (this.wereAllSavedSources !== areAllSavedSources) {
            this.wereAllSavedSources = areAllSavedSources;
            if (!areAllSavedSources) {
                this.seriesList.forEach(series => {
                    series.dateRange.dateType = DateQueryDateTypeEnum.PUBLISHED;
                });
            }
        }
    }

    /**
     * Adds a new series for multi-series blocks
     */
    addSeries(): void {
        const newSeries = new ReportSeriesModel(null, this.block.type.key as ReportBlockTypeEnum);
        newSeries.dateRange = new ReportDateRangeModel(this.seriesList[0].toApiModel().dateRange);
        newSeries.color = this.defaultColors[this.seriesList.length];
        newSeries.type = this.seriesList?.[this.seriesList?.length - 1].type ? this.seriesList[this.seriesList.length - 1].type : this.field.typeOptions[0];
        this.seriesList.push(newSeries);

        this.seriesList = this.seriesList.slice();
        this.changes.emit(this.seriesList);
    }

    /**
     * Returns a new ReportSeriesModel[] with default values
     *
     * @returns ReportSeriesModel[]
     */
    getDefaultSeriesList(seriesType?: SeriesTypeEnum): ReportSeriesModel[] {
        let blockTypeKey = null;

        if (this.block && this.block.type && this.block.type.key) {
            blockTypeKey = this.block.type.key;
        }

        const series = new ReportSeriesModel(null, blockTypeKey, seriesType);
        series.color = this.defaultColors[0];

        return [series];
    }

    /**
     * Moves a series, series type and series selected option up the list
     *
     * @param index - the series to move
     */
    moveSeriesUp(index: number) {
        if (index === 0) {
            return;
        }

        this.seriesList = this.moveUp(this.seriesList, index);
        this.changes.emit(this.seriesList.slice());
    }

    /**
     * Moves a series, series type and series selected option down the list
     *
     * @param index - the series to move
     */
    moveSeriesDown(index: number): void {
        this.seriesList = this.moveDown(this.seriesList, index);
        this.changes.emit(this.seriesList.slice());
    }

    /**
     * Removes a series from seriesList
     *
     * @param index - index of series to remove
     */
    removeSeries(index: number): void {
        this.seriesList.splice(index, 1);
        this.seriesList = this.seriesList.slice();
        this.changes.emit(this.seriesList);
    }

    /**
     * Moves an item in an array down one position
     *
     * @param array - list to change order of
     * @param index - index of item to move down
     *
     * @returns any[] -  the array that was manipulated
     */
    moveDown(array: any[], index: number) {
        if (array && index === array.length - 1) {
            return;
        }
        const tempArray = array.slice();

        const  itemToMoveDown = tempArray[index];
        const itemToMoveUp = tempArray[index + 1];

        tempArray.splice(index + 1, 1, itemToMoveDown);
        tempArray.splice(index, 1, itemToMoveUp);

        return tempArray;
    }

    /**
     * Moves an item in an array up one position
     *
     * @param array - list to change order of
     * @param index - index of item to move up
     *
     * @returns any[] -  the array that was manipulated
     */
    moveUp(array: any[], index: number): any[] {
        if (!array || index === 0) {
            return;
        }

        const tempArray = array.slice();

        const itemToMoveUp = tempArray[index];
        const itemToMoveDown = tempArray[index - 1];

        tempArray.splice(index, 1, itemToMoveDown);
        tempArray.splice(index - 1, 1, itemToMoveUp);

        return tempArray;
    }

    /**
     * Opens new window and navigates to series source
     *
     * @param cloned - the data to view the source of
     */
    goToSeriesSource(series: ReportSeriesModel, $event: Event): void {
        /**
         * If `series.mentions.combinedQuery` exists, it replaces `series.mentions.filters.searchCriteria.query` in `series.getMentionSeriesUrl()`.
         * As a result, both the source filters and the series-applied filters are applied.
         * Therefore, `combinedQuery` should be removed when generating the series URL.
         */
        const seriesUrl = series.getSeriesUrl(false);
        if (seriesUrl) {
            window.open(this.appBaseHref + seriesUrl);
        } else if (this.seriesList && this.seriesList[0]?.mentions?.socialTopicId) {
            const io: ConfirmDialogModel = {
                inputs: {
                    heading: 'Reports.SocialTopicNotViewable.heading',
                    message: 'Reports.SocialTopicNotViewable.message',
                    alertType: messageSeverityEnum.success,
                    okLabel: 'Reports.SocialTopicNotViewable.okLabel',
                    id: 'SocialTopicNotViewable'
                },
                outputs: {
                    dialogClose: () => {}
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        }
        ($event?.currentTarget as HTMLElement)?.blur();
    }

    itemChanged(): void {
        this.checkDateRange();
        this.checkDateType();
        this.seriesList = this.seriesList.slice();

        this.changes.emit(this.seriesList);
    }

    onDragStartSeries(series: ReportSeriesModel): void {
        this.draggingSeries = series;
    }

    onInputFocus() {
        this.isInputFocused = true;
    }

    onInputBlur() {
        this.isInputFocused = false;
    }

    cleanupAfterDragCompleted(event: any): void {
        this.draggingSeries = null;
        // need to clean up pointerEvents manually because ngx-drag-drop does not do it well
        // need to wait to set it back after drag and drop process completes
        // see https://github.com/reppners/ngx-drag-drop/issues/156
        setTimeout(() => {
            if (event?.target?.style) {
                event.target.style.pointerEvents = 'unset';
            }
        }, 500);
    }

    onDropSeries(event: DndDropEvent): void {
        if (event.index === undefined || event.index === null) {
            return;
        }

        let index = event.index;
        const seriesList = this.seriesList.slice();
        const indexOfSeries = seriesList.indexOf(this.draggingSeries);

        seriesList.splice(indexOfSeries, 1);
        if (index > indexOfSeries) {
            index--;
        }
        seriesList.splice(index, 0, this.draggingSeries);
        this.seriesList = seriesList.slice();

        this.changes.emit(this.seriesList.slice());
    }
}
